import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["bar", "items", "count", "modal", "modalBody", "title", "backdrop", "panel"]
  static values = {
    type: { type: String, default: "Doctor" }
  }

  connect() {
    this._onUpdate = () => this.render()
    window.addEventListener("compare:updated", this._onUpdate)
    window.addEventListener("storage", this._onUpdate)
    if (this.hasTitleTarget) {
      this.titleTarget.innerText = this.typeValue === "Hospital" ? (window.MediFyrI18n?.t?.compare_hospitals || "Compare hospitals") : (window.MediFyrI18n?.t?.compare_doctors || "Compare doctors")
    }
    this.render()
  }

  disconnect() {
    window.removeEventListener("compare:updated", this._onUpdate)
    window.removeEventListener("storage", this._onUpdate)
  }

  get storageKey() {
    return this.typeValue === "Hospital" ? "medifyr_compare_hospitals" : "medifyr_compare_doctors"
  }

  list() {
    const raw = JSON.parse(localStorage.getItem(this.storageKey) || "[]")
    return raw.filter(d => d && typeof d === "object" && d.id)
  }

  save(list) {
    localStorage.setItem(this.storageKey, JSON.stringify(list))
    window.dispatchEvent(new CustomEvent("compare:updated"))
  }

  remove(event) {
    const id = event.currentTarget.dataset.id
    this.save(this.list().filter(d => d.id !== id))
  }

  clear() {
    this.save([])
    this.closeModal()
  }

  render() {
    const list = this.list()
    if (!list.length) {
      this.barTarget.classList.add("hidden")
      this.closeModal()
      return
    }
    this.barTarget.classList.remove("hidden")
    if (this.hasCountTarget) this.countTarget.innerText = list.length

    this.itemsTarget.innerHTML = list.map(d => `
      <div class="relative shrink-0" title="${this._esc(d.name)}">
        <img src="${this._esc(d.photo)}" alt="${this._esc(d.name)}" class="h-10 w-10 rounded-full object-cover object-top border border-hairline-soft dark:border-hairline/40 shadow-xs">
        <button type="button" data-action="click->compare-tray#remove" data-id="${this._esc(d.id)}" aria-label="Remove" class="absolute -top-1 -right-1 h-4 w-4 flex items-center justify-center rounded-full bg-slate-900 text-white text-[11px] leading-none shadow cursor-pointer">&times;</button>
      </div>
    `).join("")

    if (this.hasModalTarget && !this.modalTarget.classList.contains("hidden")) {
      if (list.length < 2) this.closeModal()
      else this.modalBodyTarget.innerHTML = this._tableHtml(list)
    }
  }

  open() {
    const list = this.list()
    if (list.length < 2) {
      const noun = `${this.typeValue.toLowerCase()}s`
      // i18n string carries a Ruby %{type} placeholder — interpolate it here (JS doesn't).
      const tmpl = window.MediFyrI18n?.t?.add_min_compare || "Add at least 2 %{type} to compare."
      alert(tmpl.replace("%{type}", noun))
      return
    }
    this.modalBodyTarget.innerHTML = this._tableHtml(list)

    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout)
      this.closeTimeout = null
    }

    this.modalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.hasBackdropTarget) {
          this.backdropTarget.classList.remove("opacity-0")
          this.backdropTarget.classList.add("opacity-100")
        }
        if (this.hasPanelTarget) {
          this.panelTarget.classList.remove("opacity-0", "scale-95")
          this.panelTarget.classList.add("opacity-100", "scale-100")
        }
      })
    })
  }

  closeModal() {
    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.remove("opacity-100")
      this.backdropTarget.classList.add("opacity-0")
    }
    if (this.hasPanelTarget) {
      this.panelTarget.classList.remove("opacity-100", "scale-100")
      this.panelTarget.classList.add("opacity-0", "scale-95")
    }
    document.body.style.overflow = ""

    if (this.closeTimeout) clearTimeout(this.closeTimeout)
    this.closeTimeout = setTimeout(() => {
      if (this.hasModalTarget) {
        this.modalTarget.classList.add("hidden")
      }
      this.closeTimeout = null
    }, 300)
  }

  _tableHtml(list) {
    const isHospital = this.typeValue === "Hospital"
    const i18n = window.MediFyrI18n?.t || {}
    const rows = isHospital ? [
      [i18n.compare_type || "Type", d => d.specialty || "—"],
      [i18n.compare_location || "Location", d => d.hospital || "—"],
      [i18n.compare_rating || "Rating", d => (d.rating ? `★ ${d.rating}` : (i18n.not_rated || "Not rated"))]
    ] : [
      [i18n.compare_speciality || "Speciality", d => d.specialty || "—"],
      [i18n.compare_hospital || "Hospital", d => d.hospital || "—"],
      [i18n.compare_experience || "Experience", d => (d.experience ? (i18n.yrs_label || "%{count} yrs").replace("%{count}", d.experience) : "—")],
      [i18n.compare_rating || "Rating", d => (d.rating ? `★ ${d.rating}` : (i18n.not_rated || "Not rated"))],
      [i18n.compare_fee || "Consultation fee", d => d.fee || "—"]
    ]

    const head = list.map(d => `
      <th class="p-3 align-top text-left min-w-[140px]">
        <img src="${this._esc(d.photo)}" alt="${this._esc(d.name)}" class="h-16 w-16 rounded-xl object-cover object-top mb-2 border border-hairline-soft dark:border-hairline/40">
        <div class="font-bold text-ink dark:text-white text-sm leading-tight">${this._esc(d.name)}</div>
      </th>`).join("")

    const body = rows.map(([label, fn]) => `
      <tr class="border-t border-hairline-soft/60 dark:border-hairline/10">
        <td class="p-3 text-[11px] font-bold uppercase tracking-wider text-muted whitespace-nowrap align-top">${label}</td>
        ${list.map(d => `<td class="p-3 text-body-sm text-ink dark:text-white align-top">${this._esc(fn(d))}</td>`).join("")}
      </tr>`).join("")

    const cta = `
      <tr class="border-t border-hairline-soft/60 dark:border-hairline/10">
        <td class="p-3"></td>
        ${list.map(d => `<td class="p-3 align-top"><a href="${this._esc(d.url)}" class="inline-flex items-center gap-1 text-primary dark:text-blue-400 font-bold text-xs hover:underline">${this._esc(i18n.view_profile || "View profile →")}</a></td>`).join("")}
      </tr>`

    return `<table class="w-full border-collapse"><thead><tr><th class="p-3"></th>${head}</tr></thead><tbody>${body}${cta}</tbody></table>`
  }

  _esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]))
  }
}
